/**
 * @module useUserPreferences
 * @description Singleton composable for per-user server-backed preferences.
 *
 * Preferences are stored in the auth DB (user_preferences table) and loaded
 * once on init. Changes are saved to the server and kept in a local reactive
 * cache so reads are synchronous.
 *
 * Usage (raw API):
 *   const { get, set } = useUserPreferences();
 *   const cards = get("analyticsVisibleCards", defaultValue);
 *   await set("analyticsVisibleCards", newValue);
 *
 * Usage (reactive ref — recommended for view mode preferences):
 *   const { usePreference } = useUserPreferences();
 *   const viewMode = usePreference("assetsViewMode", "grid");
 *   // viewMode is a ref that auto-syncs to server on change
 *   // and updates from server when loadPreferences() completes
 *
 * Falls back to localStorage for offline resilience — if the server save
 * fails the value is still persisted locally and will be pushed on next
 * successful save.
 */
import { reactive, ref, watch } from "vue";
import api from "@/utils/api.js";

// Module-scope singleton state — shared across all consumers
const prefs = reactive({});
let loaded = false;
let loadingPromise = null;

/**
 * Load all preferences from the server into the local cache.
 * Called once; subsequent calls return the same promise.
 */
async function loadPreferences() {
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    try {
      const res = await api.authApi.get("/api/auth/preferences");
      const data = res.data?.data || {};
      // Merge server data into reactive cache
      for (const [key, value] of Object.entries(data)) {
        prefs[key] = value;
      }
      loaded = true;
    } catch (err) {
      console.warn("Failed to load user preferences:", err.message);
      // Fall back to localStorage values already in cache
    }
  })();
  return loadingPromise;
}

/**
 * Get a preference value. Returns the cached value or defaultValue if not set.
 * On first call triggers a server load (non-blocking — returns default until loaded).
 */
function get(key, defaultValue = null) {
  // Kick off load if not yet started
  if (!loaded && !loadingPromise) loadPreferences();
  return prefs[key] !== undefined ? prefs[key] : defaultValue;
}

/**
 * Set a preference value. Saves to server and updates local cache.
 * Also mirrors to localStorage as a fallback.
 */
async function set(key, value) {
  // Update local cache immediately for responsiveness
  prefs[key] = value;
  // Mirror to localStorage as fallback
  try {
    localStorage.setItem(`pref_${key}`, JSON.stringify(value));
  } catch { /* quota exceeded etc. */ }
  // Persist to server
  try {
    await api.authApi.put(`/api/auth/preferences/${encodeURIComponent(key)}`, { value });
  } catch (err) {
    console.warn(`Failed to save preference "${key}":`, err.message);
  }
}

/**
 * Create a reactive ref that auto-syncs with server-backed preferences.
 *
 * Initializes from (in priority order):
 *   1. Server cache (prefs reactive object)
 *   2. Old localStorage key (migration from pre-server-backed pattern)
 *   3. Provided default value
 *
 * When the user changes the ref locally, it saves to server automatically.
 * When server preferences load (after login), the ref updates to match.
 *
 * @param {string} key - Preference key
 * @param {*} defaultValue - Fallback value if no preference is stored
 * @param {string} [legacyLocalStorageKey] - Old localStorage key to migrate from
 *                                           (defaults to same as key)
 * @returns {import('vue').Ref} Reactive ref synced with server
 */
function usePreference(key, defaultValue, legacyLocalStorageKey) {
  const lsKey = legacyLocalStorageKey || key;

  // Resolve initial value from cache, then localStorage migration, then default
  let initial;
  if (prefs[key] !== undefined) {
    initial = prefs[key];
  } else {
    // Check legacy localStorage key (for migration from old client-only pattern)
    const stored = localStorage.getItem(lsKey);
    if (stored !== null) {
      try { initial = JSON.parse(stored); } catch { initial = stored; }
    } else {
      // Check pref_-prefixed key (used by set() as fallback)
      const prefStored = localStorage.getItem(`pref_${key}`);
      if (prefStored !== null) {
        try { initial = JSON.parse(prefStored); } catch { initial = prefStored; }
      } else {
        initial = defaultValue;
      }
    }
  }

  const value = ref(initial);

  // Guard to prevent save-back when server pushes an update
  let suppressSave = false;

  // When server preferences load, update the ref (without triggering a save back)
  watch(() => prefs[key], (newVal) => {
    if (newVal !== undefined && newVal !== value.value) {
      suppressSave = true;
      value.value = newVal;
    }
  });

  // When local value changes, save to server (unless triggered by server load)
  watch(value, (newVal) => {
    if (suppressSave) {
      suppressSave = false;
      return;
    }
    set(key, newVal);
  });

  return value;
}

/**
 * Reset the singleton state — call on logout so the next user gets a fresh load.
 */
function resetPreferences() {
  for (const key of Object.keys(prefs)) delete prefs[key];
  loaded = false;
  loadingPromise = null;
}

export function useUserPreferences() {
  return { prefs, get, set, usePreference, loadPreferences, resetPreferences };
}
